import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useProfileImage } from "src/utils/useProfileImage.js";
import { getProcessedImage, useProcessedImage } from "src/utils/useProcessedImage.js";
import { imageDecisions } from "src/utils/imageDecisionStore.js";
import {
  bakedTileCandidates,
  calendarProfileImageUrl,
  imageIdentity,
  resizedImageCandidates,
} from "src/utils/imageUrl.js";

/** Session cache holding the painted value, which may be a processed object URL. */
const displayImageCache = new Map(); // cacheKey → winning displayImage (null = basketball)
const MAX_DISPLAY_ENTRIES = 300;

/** Bounded insert — evicts oldest entries; mounted consumers keep their strings. */
const rememberDisplayImage = (key, value) => {
  displayImageCache.delete(key);
  displayImageCache.set(key, value);
  while (displayImageCache.size > MAX_DISPLAY_ENTRIES) {
    displayImageCache.delete(displayImageCache.keys().next().value);
  }
};

/**
 * Resolves the image a card paints, walking a per-type fallback queue.
 *
 * @param {{ type?: string, calendar: object, homeTeamLogo?: string,
 *   awayTeamLogo?: string, eventImageUrl?: string, background?: string,
 *   bakedTile?: boolean }} params - `bakedTile` leads the queue with the
 *   server-baked composite; only for consumers that render cover-fill.
 */
export const useDisplayImage = ({
  type = "RecommendedCalendar",
  calendar,
  homeTeamLogo,
  awayTeamLogo,
  eventImageUrl,
  background,
  bakedTile = false,
}) => {
  const profileImage = useProfileImage({
    picture: calendarProfileImageUrl(calendar),
  });

  const imagesSrcRef = useRef([]);

  // A calendar's display name often differs from its event team-logo filename
  // (for example, a league-wide schedule). An explicit event logo is still a
  // valid fallback image, so don't discard it based on that filename match.
  const homeTeamLogoChecked = homeTeamLogo || null;

  const shouldProcessLogo =
    type === "RecommendedCalendar" ? homeTeamLogoChecked : null;

  // Poster tiles paint their `background` art, which needs no background
  // removal — and their bucket profile is routinely absent. Skip the eager
  // fetch + canvas pass; the raw profile URL stays in the fallback queue.
  const shouldProcessProfile = background ? null : profileImage;

  const { processedImage: processedHomeTeamLogo, loading: logoProcessing } =
    useProcessedImage(shouldProcessLogo);
  const { processedImage: processedProfileImage, loading: profileProcessing } =
    useProcessedImage(shouldProcessProfile);

  const isProcessingComplete = !logoProcessing && !profileProcessing;

  // Stable key per card — identifies the unique set of candidate images
  const cacheKey = useMemo(
    () =>
      [
        calendar._id || calendar.handle || "",
        imageIdentity(eventImageUrl),
        imageIdentity(homeTeamLogo),
        imageIdentity(awayTeamLogo),
        imageIdentity(background),
        bakedTile ? "baked" : "",
      ].join("|"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendar._id, calendar.handle, eventImageUrl, homeTeamLogo, awayTeamLogo, background, bakedTile]
  );

  // undefined = no cache entry, null = basketball decision, string = winning source URL.
  // Both session and persisted caches retain source URLs only; processed object URLs
  // are owned by useProcessedImage and are safe to revoke on cache eviction.
  const rawCachedDecision = displayImageCache.has(cacheKey)
    ? displayImageCache.get(cacheKey)
    : imageDecisions.has(cacheKey)
      ? imageDecisions.get(cacheKey)
      : undefined;

  // A persisted winner may carry a stale cache-busting query (?version=N from a
  // prior session). Re-link it by identity to this session's candidate URL so
  // background-removal processing and promotion key off the current URL instead
  // of painting the stale raw source forever.
  const relinkCachedDecision = (decision) => {
    if (typeof decision !== "string") return decision;
    if (profileImage && imageIdentity(decision) === imageIdentity(profileImage)) {
      return profileImage;
    }
    if (
      homeTeamLogoChecked &&
      imageIdentity(decision) === imageIdentity(homeTeamLogoChecked)
    ) {
      return homeTeamLogoChecked;
    }
    return decision;
  };
  const cachedDecision = relinkCachedDecision(rawCachedDecision);

  // Do not paint the unprocessed source. The tile remains in its stable loading
  // state until the background-removed asset (or its definitive fallback) is
  // ready, so the first visible image is also the final one.
  const isBasketballCached = cachedDecision === null;

  // An Event matchup is painted by TeamLogos from the source logos, and the card
  // recognizes it by that source, so it must never resolve to a processed URL.
  const isMatchupSource = (url) =>
    type === "Event" &&
    !!awayTeamLogo &&
    !!url &&
    imageIdentity(url) === imageIdentity(homeTeamLogo);

  // Only these two candidates get background removal, so a remembered winner
  // that is neither is already final and needs no promotion.
  const awaitsProcessing = (url) =>
    !isMatchupSource(url) &&
    (url === profileImage || url === homeTeamLogoChecked);

  // A remembered winner is stored as its source URL. Resolve it against the
  // in-memory derivatives so a warm cache paints the final asset on the very
  // first render, with no loading state in between.
  const resolvedCachedDecision =
    cachedDecision && awaitsProcessing(cachedDecision)
      ? getProcessedImage(cachedDecision)
      : cachedDecision;

  const cachedSourceNeedsPromotion =
    cachedDecision !== undefined && !resolvedCachedDecision && !isBasketballCached;
  const [loading, setLoading] = useState(
    isBasketballCached
      ? false
      : !isProcessingComplete || cachedSourceNeedsPromotion,
  );

  // Resized derivatives slot in just ahead of their raw source — a quality
  // upgrade at the background's position in line, never a priority change.
  const buildCandidateQueue = () => {
    const queue = [];
    const resolvedProfile = getProcessedImage(profileImage) ?? profileImage;
    const resolvedLogo = getProcessedImage(homeTeamLogoChecked) ?? homeTeamLogoChecked;

    if (bakedTile) queue.push(...bakedTileCandidates(calendarProfileImageUrl(calendar)));
    if (type === "RecommendedCalendar") {
      if (resolvedProfile) queue.push(resolvedProfile);
      if (resolvedLogo) queue.push(resolvedLogo);
      if (eventImageUrl) queue.push(eventImageUrl);
      if (background) queue.push(...resizedImageCandidates(background), background);
    } else if (type === "Event") {
      if (eventImageUrl) queue.push(eventImageUrl);
      if (homeTeamLogo && awayTeamLogo) queue.push(homeTeamLogo);
      if (background) queue.push(...resizedImageCandidates(background), background);
      if (resolvedProfile) queue.push(resolvedProfile);
    }
    return queue;
  };
  // Ref kept per render so the stable error handler can rebuild with current inputs.
  const buildQueueRef = useRef(buildCandidateQueue);
  buildQueueRef.current = buildCandidateQueue;

  const [displayImage, setDisplayImage] = useState(() => {
    // Decision cache hit: jump straight to the previously winning image, no queue building.
    if (cachedDecision !== undefined) {
      return resolvedCachedDecision ?? cachedDecision;
    }

    // Decision cache miss: build the fallback queue
    imagesSrcRef.current = buildCandidateQueue();
    return imagesSrcRef.current.shift() || null;
  });

  // The candidate that produced `displayImage`. A processed object URL is dead
  // after a reload, so only the source is safe to persist. The null guard keeps
  // a not-yet-processed candidate from claiming the default-art decision.
  const winningSource = !displayImage
    ? null
    : displayImage === processedProfileImage
      ? profileImage
      : displayImage === processedHomeTeamLogo
        ? homeTeamLogoChecked
        : displayImage;

  // Refs kept in sync each render so the unmount cleanup always has current values
  const displayImageRef = useRef(displayImage);
  displayImageRef.current = displayImage;
  const winningSourceRef = useRef(winningSource);
  winningSourceRef.current = winningSource;
  const cacheKeyRef = useRef(cacheKey);

  // Whether this card skipped the fallback queue. Captured per cacheKey rather
  // than per render, because settling repopulates the cache this reads.
  const startedFromCacheRef = useRef(cachedDecision !== undefined);
  // Set once a remembered winner fails, so the effects below cannot re-record
  // the verdict this mount just decided it has no basis for.
  const forgottenRef = useRef(false);
  if (cacheKeyRef.current !== cacheKey) {
    cacheKeyRef.current = cacheKey;
    startedFromCacheRef.current = cachedDecision !== undefined;
    forgottenRef.current = false;
  }


  // The processed derivative that should replace the painted source. One
  // expression feeds both the promotion and the reveal gate so they cannot
  // disagree; a skipped pass (null) must not shadow a finished one when the
  // logo and profile share a URL.
  const promotedVersion = isMatchupSource(displayImage)
    ? null
    : ((displayImage === profileImage ? processedProfileImage : null) ??
      (displayImage === homeTeamLogoChecked ? processedHomeTeamLogo : null));

  // What is on screen is a source still awaiting its processed replacement.
  const pendingPromotion =
    !!displayImage && !!promotedVersion && promotedVersion !== displayImage;

  // A cache miss begins with the source URL only while hidden by `loading`.
  // Promote it to the processed asset before the tile may become visible.
  useEffect(() => {
    if (pendingPromotion) setDisplayImage(promotedVersion);
  }, [pendingPromotion, promotedVersion]);

  // Reveal as soon as the final asset is the one committed. Gating on the
  // promotion itself replaces a fixed delay that cost every tile 300ms.
  useEffect(() => {
    if (isBasketballCached || !isProcessingComplete || pendingPromotion) return;
    setLoading(false);
  }, [isProcessingComplete, isBasketballCached, pendingPromotion]);

  // Persist the winning image so the next mount skips the fallback queue
  // entirely. Only positive winners persist across sessions — a "no image"
  // verdict may be transient (network hiccup, art uploaded later), so it lives
  // in the session cache alone and a fresh visit retries the queue.
  useEffect(() => {
    if (!loading) {
      rememberDisplayImage(cacheKey, winningSource);
      if (!forgottenRef.current) {
        if (winningSource) imageDecisions.set(cacheKey, winningSource);
        else imageDecisions.delete(cacheKey);
      }
    }
  }, [loading, displayImage, cacheKey, winningSource]);

  // Unmount write: catches fast navigation (user left before loading=false).
  // Session cache only — the candidate never proved it loads, so persisting it
  // would seed a dead URL as the remembered winner for future sessions.
  useEffect(() => {
    return () => {
      if (!displayImageCache.has(cacheKeyRef.current)) {
        rememberDisplayImage(cacheKeyRef.current, winningSourceRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Write basketball decision immediately when the queue is exhausted — don't
  // wait for processing, because the deterministic fallback is final itself.
  const handleImageError = useCallback(() => {
    // A remembered winner that fails was never compared against the other
    // candidates: forget it and resume the walk over a freshly built queue
    // (minus the failed candidate) instead of condemning the card to default art.
    if (
      imagesSrcRef.current.length === 0 &&
      startedFromCacheRef.current &&
      !forgottenRef.current
    ) {
      forgottenRef.current = true;
      imageDecisions.delete(cacheKeyRef.current);
      imagesSrcRef.current = buildQueueRef.current().filter(
        (url) => url !== displayImageRef.current,
      );
      const retry = imagesSrcRef.current.shift();
      if (retry) return setDisplayImage(retry);
    }

    if (imagesSrcRef.current.length === 0) {
      rememberDisplayImage(cacheKeyRef.current, null);
      if (startedFromCacheRef.current) forgottenRef.current = true;
      imageDecisions.delete(cacheKeyRef.current);
      setLoading(false);
      return setDisplayImage(null);
    }
    const nextImage = imagesSrcRef.current.shift();
    setDisplayImage(nextImage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    displayImage,
    setDisplayImage,
    loading,
    handleImageError,
  };
};
